import React, { ReactNode } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Divider
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import { green } from "@mui/material/colors";

import { TSalarySummary } from "../../../../interfaces/SalaryInterface";
import { RetirementFundType, TRetirementFundType } from "../../../../constants/payroll";
import {
  ProvidentFundContribution,
  SocialSecurityFundContribution
} from "../../../../config/payroll";
import { roundTo } from "../../../../helpers/number";
import { rupeesDisplayFull } from "../../../../helpers/util";

interface PropType {
  salarySummary: TSalarySummary;
  pfSsfLabel: string;
  fundType: Exclude<TRetirementFundType, typeof RetirementFundType.CITIZEN_INVESTMENT_TRUST>;
}

const AnnualDeductions = ({ salarySummary, pfSsfLabel = "", fundType }: PropType): ReactNode => {
  const ssfAmount = salarySummary?.retirementContributions?.annualRetirementContribution || 0;
  const oneThird = (salarySummary?.taxableIncome?.annualGrossTaxableIncome || 0) / 3;
  const maxLimit = 500000;

  const deductions = [
    {
      key: "SSF",
      label: fundType
        ? `Annual ${pfSsfLabel} Contribution (Employee ${
            fundType === RetirementFundType.PROVIDENT_FUND
              ? ProvidentFundContribution.employee
              : SocialSecurityFundContribution.employee
          }% + Employer ${
            fundType === RetirementFundType.PROVIDENT_FUND
              ? ProvidentFundContribution.employer
              : SocialSecurityFundContribution.employer
          }%)`
        : "Employee Contributions",
      tooltip: (
        <Box sx={{ minWidth: 250 }}>
          {/* past contributions total */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2">{`Past ${pfSsfLabel} Contributions Total`}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupeesDisplayFull(
                salarySummary?.retirementContributions?.summary
                  .employeePastRetirementContributionTotal +
                  salarySummary?.retirementContributions?.summary
                    .employerPastRetirementContributionTotal
              )}
            </Typography>
          </Box>
          {/* present contributions total */}
          <Divider sx={{ my: 0.5 }} />\
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2">{`Current ${pfSsfLabel} Contribution Total`}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupeesDisplayFull(
                salarySummary?.retirementContributions?.summary
                  .employeeCurrentRetirementContribution +
                  salarySummary?.retirementContributions?.summary
                    .employerCurrentRetirementContribution
              )}
            </Typography>
          </Box>
          {/* future contributions total */}
          <Divider sx={{ my: 0.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="body2">{`Future ${pfSsfLabel} Contribution Total`}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupeesDisplayFull(
                salarySummary?.retirementContributions?.summary
                  .employeeFutureRetirementContributionTotal +
                  salarySummary?.retirementContributions?.summary
                    .employerFutureRetirementContributionTotal
              )}
            </Typography>
          </Box>
        </Box>
      ),
      amount: roundTo(ssfAmount)
    },
    {
      key: "ONE_THIRD",
      label: "1/3 or annual assessable income",
      tooltip: "One-third of annual assessable income or annual gross taxable income",
      amount: roundTo(oneThird)
    },
    {
      key: "MAX",
      label: "Max 5 Lakh",
      tooltip: "Statutory maximum deduction limit",
      amount: roundTo(maxLimit)
    }
  ];

  // Find the minimum deduction
  const selected = deductions.reduce((min, d) => (d.amount < min.amount ? d : min));

  return (
    <Box>
      <Typography>Annual Deductions</Typography>
      <TableContainer
        component={Paper}
        sx={{ mt: 0.5, boxShadow: "none", border: "1px solid", borderColor: "divider" }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ textAlign: "left" }}>Topic</TableCell>
              <TableCell sx={{ textAlign: "right" }}>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deductions.map((d) => {
              const isSelected = d.key === selected.key;

              return (
                <TableRow
                  key={d.key}
                  sx={{
                    bgcolor: isSelected ? "rgba(76,175,80,0.08)" : "inherit",
                    fontWeight: isSelected ? 600 : 400
                  }}
                >
                  <TableCell
                    sx={(theme) => ({
                      display: "flex",
                      alignItems: "center",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? theme.palette.primary.main : "inherit"
                    })}
                  >
                    {d.label}
                    <Tooltip
                      placement="top"
                      slotProps={{
                        popper: {
                          modifiers: [{ name: "offset", options: { offset: [0, 8] } }]
                        },
                        tooltip: {
                          sx: {
                            backgroundColor: "rgba(66,66,66,0.95)",
                            color: "#fff"
                          }
                        }
                      }}
                      title={d.tooltip}
                    >
                      <IconButton size="small" sx={{ ml: 0.5 }}>
                        <InfoIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>

                    {isSelected && (
                      <CheckCircleIcon sx={{ ml: 1, color: green[500], fontSize: 18 }} />
                    )}
                  </TableCell>

                  <TableCell
                    sx={(theme) => ({
                      textAlign: "right",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? theme.palette.primary.main : "inherit"
                    })}
                  >
                    {rupeesDisplayFull(d.amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AnnualDeductions;
